import { createFileRoute } from '@tanstack/react-router'
import { useSelector, useDispatch } from 'react-redux'
import { Button } from '@/components/ui/button'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useState } from 'react'
import { toast } from 'sonner'
import { addFeedback, updateFeedback } from '@/store/actions'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { ThumbsUp, ThumbsDown } from 'lucide-react'
import { selectJobOverviewData, selectCheckinByBooking } from '@/store/selectors'
import { RootState } from '@/store/store'

export const Route = createFileRoute('/job/$jobId/$date/signoff/questions')({
  component: JobSignOffQuestionsPage,
})

interface Question {
  id: string
  question: string
  answer: boolean | null
}

function JobSignOffQuestionsPage() {
  const { jobId, date } = Route.useParams()
  const navigate = Route.useNavigate()
  const dispatch = useDispatch()

  const [questions, setQuestions] = useState<Question[]>([
    {
      id: 'question_1',
      question: 'Did the operative arrive at your property on time?',
      answer: null,
    },
    {
      id: 'question_2',
      question: 'Was the operative polite and professional?',
      answer: null,
    },
    {
      id: 'question_3',
      question: 'Was the operative clean and tidy?',
      answer: null,
    },
    {
      id: 'question_4',
      question: 'Are you happy with the quality of works carried out by the operative?',
      answer: null,
    },
  ])

  const [showIncompleteDialog, setShowIncompleteDialog] = useState(false)

  const { job, booking } = useSelector((state: RootState) =>
    selectJobOverviewData(state, jobId)
  )

  const checkin = useSelector((state: RootState) =>
    booking ? selectCheckinByBooking(state, booking.id, booking.date) : null
  )

  const feedbackToday = useSelector((state: RootState) =>
    state.feedback.items.find(feedback =>
      booking &&
      feedback.booking_id === booking.id &&
      feedback.booking_date === booking.date
    )
  )

  const updateAnswer = (idx: number, value: boolean | null) => {
    const newQuestions = [...questions]
    newQuestions[idx] = { ...questions[idx], answer: value }
    setQuestions(newQuestions)
  }

  const handleProceed = (skipped = false) => {
    const notAnswered = questions.filter(item => item.answer === null)

    if (!skipped && notAnswered.length > 0) {
      setShowIncompleteDialog(true)
      return
    }

    // Save answers
    if (!skipped) {
      const answers: Record<string, boolean> = {}
      questions.forEach(answer => {
        if (answer.answer !== null) {
          answers[answer.id] = answer.answer
        }
      })

      if (feedbackToday) {
        dispatch(updateFeedback({
          ...feedbackToday,
          feedback: feedbackToday.feedback || 'survey',
          ...answers,
        }))
      } else if (job && booking && checkin) {
        dispatch(addFeedback({
          ...answers,
          feedback: 'survey',
          job_id: job.id,
          checkin_id: checkin.id,
          booking_id: booking.id,
          booking_date: booking.date,
          company_id: job.company_id,
        }))
      }
    } else {
      // Handle skip - create minimal feedback entry
      if (!feedbackToday && job && booking && checkin) {
        dispatch(addFeedback({
          feedback: 'survey',
          job_id: job.id,
          checkin_id: checkin.id,
          booking_id: booking.id,
          booking_date: booking.date,
          company_id: job.company_id,
        }))
      }
    }

    navigate({ to: '/job/$jobId/$date/signoff/signature', params: { jobId, date } })
  }

  if (!job || !booking) {
    return <div>Job not found</div>
  }

  return (
    <>
      {/* Content */}
    <ScrollArea className="col-span-2 h-full overflow-y-auto overflow-x-hidden max-h-[calc(100vh-5rem)]">
      <div className="p-8 flex items-center">
        <div className="max-w-4xl w-full space-y-8 mx-auto">
            <div className="px-8 py-6 flex flex-col justify-center min-h-full">
              <div className="mb-8">
                <h2 className="text-3xl font-bold text-gray-900 mb-4">
                  Please answer the following questions
                </h2>
                <p className="text-gray-600">
                  For your piece of mind, any answers you give CANNOT be viewed by the operative once you have submitted them.
                </p>
              </div>

              <div className="space-y-2 mb-8">
                {questions.map((question, idx) => (
                  <div
                    key={`question-${idx}`}
                    className="flex items-center bg-gray-50 border border-gray-200 rounded-lg"
                  >
                    {/* Thumbs Up */}
                    <button
                      onClick={() => updateAnswer(idx, question.answer === true ? null : true)}
                      className="p-3 hover:bg-gray-100 rounded-l-lg transition-colors"
                    >
                      <ThumbsUp
                        className={`w-6 h-6 ${
                          question.answer === true
                            ? 'text-green-600 fill-green-600'
                            : 'text-gray-400'
                        }`}
                      />
                    </button>

                    {/* Thumbs Down */}
                    <button
                      onClick={() => updateAnswer(idx, question.answer === false ? null : false)}
                      className="p-3 hover:bg-gray-100 transition-colors"
                    >
                      <ThumbsDown
                        className={`w-6 h-6 ${
                          question.answer === false
                            ? 'text-red-600 fill-red-600'
                            : 'text-gray-400'
                        }`}
                      />
                    </button>

                    {/* Question Text */}
                    <p className="flex-1 p-3 text-gray-900 leading-normal m-0">
                      {question.question}
                    </p>
                  </div>
                ))}
              </div>

              <div className="flex justify-between items-center">
                <Button onClick={() => handleProceed(false)}>
                  Proceed
                </Button>
                <Button
                  variant="link"
                  onClick={() => handleProceed(true)}
                >
                  Skip
                </Button>
              </div>
            </div>
        </div>
      </div>
          </ScrollArea>

      {/* Incomplete Dialog */}
      <Dialog open={showIncompleteDialog} onOpenChange={setShowIncompleteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-normal text-center">
              We kindly ask you to leave feedback…
            </DialogTitle>
            <DialogDescription className="text-center text-gray-900">
              Please complete our 4 simple questions for us. Alternatively click the skip button.
            </DialogDescription>
          </DialogHeader>

          <div className="flex justify-end gap-2">
            <Button onClick={() => setShowIncompleteDialog(false)}>
              Continue
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  )
}
